import { useEffect, useState } from "react";
import { generateCodeChallenge, generateRandomString } from "../utils/pkce";

const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_SPOTIFY_REDIRECT_URI;

export const useSpotifyAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [spotifyUserInfo, setSpotifyUserInfo] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [playlistCount, setPlaylistCount] = useState(null);
  const [spotifyPlaylist, setSpotifyPlaylist] = useState([]);

  let isRefreshing = false;

  const refreshAccessToken = async () => {
    if (isRefreshing) return null;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      isRefreshing = false;
      return null;
    }

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: CLIENT_ID,
    });

    try {
      const res = await fetch("https://accounts.spotify.com/api/token", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const data = await res.json();

      if (data.access_token) {
        localStorage.setItem("accessToken", data.access_token);
        if (data.expires_in) {
          localStorage.setItem("expiresIn", data.expires_in.toString());
        }
        setAccessToken(data.access_token);
        isRefreshing = false;
        return data.access_token;
      } else {
        isRefreshing = false;
        return null;
      }
    } catch (error) {
      console.error("Refresh token error:", error);
      isRefreshing = false;
      return null;
    }
  };

  const validateAccessToken = async (token) => {
    const res = await fetch("https://api.spotify.com/v1/me", {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 401) {
      const newToken = await refreshAccessToken();
      return newToken;
    }

    return token;
  };

  const exchangeCodeForToken = async (code) => {
    const verifier = localStorage.getItem("code_verifier");
    if (!verifier) return;

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: verifier,
    });

    const res = await fetch("https://accounts.spotify.com/api/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    const data = await res.json();
    if (!data.access_token) return;

    localStorage.setItem("accessToken", data.access_token);
    localStorage.setItem("refreshToken", data.refresh_token);
    localStorage.setItem("expiresIn", data.expires_in.toString());
    setAccessToken(data.access_token);
    setIsLoggedIn(true);

    window.history.replaceState({}, document.title, REDIRECT_URI);
  };

  useEffect(() => {
    const checkAccessToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code) {
        await exchangeCodeForToken(code);
      } else {
        let token = localStorage.getItem("accessToken");
        if (token) {
          token = await validateAccessToken(token);
          if (token) {
            setAccessToken(token);
            setIsLoggedIn(true);
          }
        }
      }
    };

    checkAccessToken();
  }, []);

  // ✅ This effect will fetch user and playlists only once when the token is available
  const fetchDataSpotify = async () => {
    if (!accessToken) return;

    try {
      const [userRes, playlistRes] = await Promise.all([
        fetch("https://api.spotify.com/v1/me", {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch("https://api.spotify.com/v1/me/playlists", {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      const userData = await userRes.json();
      const playlistData = await playlistRes.json();

      if (!userData.error) {
        setSpotifyUserInfo(userData);
      }

      setSpotifyPlaylist(playlistData.items || []);
      setPlaylistCount(playlistData.total || 0);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    if (isLoggedIn && accessToken) {
      fetchDataSpotify();
    }
  }, [isLoggedIn, accessToken]);

  const spotifyLogin = async () => {
    const verifier = generateRandomString(128);
    const challenge = await generateCodeChallenge(verifier);
    const state = generateRandomString(16);
    const scope = [
      "user-read-currently-playing",
      "user-read-playback-state",
      "playlist-read-private",
      "playlist-modify-private",
      "playlist-modify-public",
      "user-read-private",
      "user-read-email",
      "user-read-recently-played",
      "user-top-read",
      "user-read-playback-position",
    ].join(" ");

    localStorage.setItem("code_verifier", verifier);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: CLIENT_ID,
      scope,
      redirect_uri: REDIRECT_URI,
      state,
      code_challenge_method: "S256",
      code_challenge: challenge,
    });

    window.location.href = `https://accounts.spotify.com/authorize?${params.toString()}`;
  };

  const spotifyLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("expiresIn");
    localStorage.removeItem("code_verifier");

    setAccessToken(null);
    setIsLoggedIn(false);
    setSpotifyUserInfo(null);
    setPlaylistCount(null);
    setSpotifyPlaylist([]);

    window.location.href = REDIRECT_URI;
  };

  return {
    isLoggedIn,
    spotifyUserInfo,
    spotifyLogin,
    spotifyLogout,
    accessToken,
    playlistCount,
    spotifyPlaylist,
    fetchDataSpotify
  };
};
